<script lang="ts">
    import { focusTrap } from "@ui-utils/actions/focusTrap";
    import CN from "classnames";
    import { SvelteComponent, createEventDispatcher } from "svelte";
    import { quintIn, quintOut } from "svelte/easing";
    import { fly } from "svelte/transition";

    import ButtonWithIcon from "@components/Buttons/ButtonWithIcon.svelte";
    import Icon from "@components/Icon.svelte";
    import CloseIcon from "@components/icons/CloseIcon.svelte";

    import { MODAL_CLOSE_EVENT } from "./constants";

    let className: string | undefined = undefined;
    export let modalBodyClass: string | undefined = undefined;
    export let title: string | undefined = undefined;
    export let style: string | undefined = undefined;
    export let variant: "" | "fullscreen" = "";
    export let closeIcon: typeof SvelteComponent | undefined = undefined;
    export { className as class };
    export let disableCloseButton = false;
    export let isOpen: boolean;
    export let disableFocusTrap = false;

    const dispatcher = createEventDispatcher();
    const handleClose = () => dispatcher(MODAL_CLOSE_EVENT);
</script>

<div
    {style}
    class={CN("gw-modal-dialog", className)}
    class:gw-modal-dialog--fullscreen={variant === "fullscreen"}
    in:fly={{
        delay: 250,
        duration: 200,
        x: 0,
        y: 100,
        opacity: 0,
        easing: quintOut,
    }}
    out:fly={{ duration: 100, x: 0, y: 50, opacity: 0, easing: quintIn }}
    use:focusTrap={{
        enabled: !disableFocusTrap && isOpen,
        options: { clickOutsideDeactivates: true },
    }}
>
    {#if title}
        <div class="gw-modal-header">
            <div class="gw-modal-header__title">{title}</div>
        </div>
    {/if}

    <div class={CN("gw-modal-body", modalBodyClass)}>
        <slot />
    </div>

    {#if !disableCloseButton}
        <ButtonWithIcon
            class="gw-modal-header__close_btn gw-modal-header__close_btn--absolute"
            on:click={handleClose}
            ariaLabel="Close modal"
        >
            {#if closeIcon}
                <svelte:component this={closeIcon} />
            {:else}
                <Icon height="22" width="22">
                    <CloseIcon />
                </Icon>
            {/if}
        </ButtonWithIcon>
    {/if}
</div>

<style lang="postcss">
    .gw-modal-dialog {
        position: relative;
        max-width: 85vw;
        margin: 0.9em 0;
        padding: 1em;
        transition-property: all;
        transition-duration: var(--gw-default-transition-duration);
        transition-timing-function: var(--gw-default-transition-fn);
        border-radius: var(--gw-border-radius-md);
        background-color: var(--gw-modal-dialog-bg);
        box-shadow: var(--gw-modal-shadow);
        box-sizing: border-box;

        &.gw-modal-dialog--fullscreen {
            max-width: 100%;
            padding: 0;
        }

        @media screen and (max-width: 400px) {
            margin: 0;
            max-width: 100vw;
            width: 100vw;
            border-radius: 0;
        }
    }

    .gw-modal-header {
        display: flex;
        box-sizing: border-box;
        justify-content: space-between;
        padding: var(--gw-modal-padding);
        border-bottom: 1px solid var(--gw-modal-borders-color);
    }

    .gw-modal-header__title {
        font-size: 1.2rem;
        font-weight: 500;
    }

    .gw-modal-dialog :global(.gw-modal-header__close_btn--absolute) {
        position: absolute;
        top: 0.5em;
        right: 0.5em;
        width: 2.5em;
        height: 2.5em;
    }

    .gw-modal-body {
        padding: var(--gw-modal-padding);
    }
</style>
